import { DocumentMeta } from '../meta';
import { RightType } from '../static/types';
import { toFixedPercentage } from '../utils';
import { ActionName, IncomeAction } from './action';
import {
  Condition,
  ConditionGroup,
  ConditionWithTarget,
  getTakeFromUntilCondition,
  isCondition,
  isConditionWithTarget,
  TakeFromUntilConditions,
  and,
  checkCondition,
  skipTakeFromUntilConditions
} from './conditions';
import { nodeExists, getNode, isVerticalGroup, isRight } from './node';
import { RightState, TitleState } from './state';
import { toFixed } from './statement';
import { WaterfallSource } from './waterfall';
import { sum } from '../utils';

export interface Right {
  _meta?: DocumentMeta;
  id: string;
  type: RightType,
  name: string;
  contractId?: string;
  actionName: ActionName;
  groupId: string;
  order: number; // Used for vertical group childs (steps), set to zero for others
  /** Parents (above) of this right */
  nextIds: string[];
  rightholderId: string;
  percent: number; // Between 0 and 100
  conditions?: ConditionGroup;
  version: Record<string, RightVersion>;
  blameId?: string;
  pools: string[];
  collapsed: Record<string, boolean>; // Used for horizontal group collapsed state. Key is current user id.
}

export interface RightVersion {
  standalone?: true;
  percent?: number;
  conditions?: ConditionGroup;
}

export interface RightOverride {
  incomeId: string;
  rightId: string;
  percent: number;
  initial: number;
  comment: string;
}

export function createRight(params: Partial<Right> = {}) {
  const right: Right = {
    id: '',
    type: 'unknown',
    name: '',
    actionName: 'prepend',
    groupId: '',
    order: 0,
    nextIds: [],
    rightholderId: '',
    percent: 100,
    pools: [],
    version: {},
    collapsed: {},
    ...params
  }

  if (!right.name) right.name = right.id;

  return right;
}

export function createRightOverride(params: Partial<RightOverride> = {}): RightOverride {
  return {
    incomeId: '',
    rightId: '',
    percent: 100,
    initial: 100,
    comment: '',
    ...params
  }
}

/**
 * Order rights by their nextIds.
 * For waterfall rights creation, we need them ordered (prepend)
 * @param rights 
 * @returns 
 */
export function orderRights(rights: Right[]): Right[] {
  const allNextIds = Array.from(new Set(rights.map(right => right.nextIds).flat()));
  const notMatching = allNextIds.filter(id => !rights.map(right => right.id).includes(id))
  if (notMatching.length) throw new Error(`Some nextIds do not match any right id : ${notMatching}`);

  const rootRights = rights.filter(right => !right.nextIds.length);
  let childRights = rights.filter(right => right.nextIds.length);
  const orderedRights = rootRights;

  while (childRights.length > 0) {
    const nextRights = childRights.filter(r => r.nextIds.every(nextId => orderedRights.map(i => i.id).includes(nextId)));
    childRights = childRights.filter(r => !nextRights.includes(r));
    orderedRights.push(...nextRights);
  }

  return orderedRights;
}

export function getRightConditions(right: Right | RightState) {
  return (right?.conditions?.conditions?.filter(c => isCondition(c)).filter(c => !!c) || []) as Condition[];
}

export function skipGroups(rights: Right[]) {
  // Groups are skipped here and revenue will be re-calculated from the childrens
  const groupRightTypes: RightType[] = ['horizontal', 'vertical'];
  return rights.filter(r => !groupRightTypes.includes(r.type));
}

/**
 * Return current right and its childs (excluding groups but with groups members)
 * @param rightId
 * @param rights 
 */
export function getChilds(rightId: string, rights: Right[]): Right[] {
  const right = rights.find(r => r.id === rightId);
  if (!right) return [];
  const _childs = [..._getChilds(rightId, rights), right];
  const childs: Right[] = [];
  for (const child of _childs) {
    if (isGroup(child)) {
      const subChilds = rights.filter(r => r.groupId === child.id);
      for (const schild of subChilds) {
        if (isGroup(schild)) {
          childs.push(...rights.filter(r => r.groupId === schild.id));
        } else {
          childs.push(schild);
        }
      }
    } else {
      childs.push(child);
    }
  }

  const uniqueChilds = Array.from(new Set(childs.map(c => c.id))).map(id => childs.find(c => c.id === id));
  return uniqueChilds.filter(c => !!c);
}

export function getSingleSourceChildIds(sourceId: string, rights: Right[], sources: WaterfallSource[]) {
  const source = sources.find(s => s.id === sourceId);
  if (!source) return [];
  const sourceChildIds = Array.from(new Set(getChilds(source.destinationId, rights).map(c => c.id)));

  const otherSources = sources.filter(s => s.id !== sourceId);
  const otherTopLevelRigthIds = otherSources.map(s => s.destinationId);
  const otherChildIds = Array.from(new Set(otherTopLevelRigthIds.map(id => getChilds(id, rights).map(c => c.id)).flat()));
  return sourceChildIds.filter(id => !otherChildIds.includes(id));
}

export function isLinkedToSource(rightId: string, rights: Right[], sourceId: string, sources: WaterfallSource[]) {
  const source = sources.find(s => s.id === sourceId);
  if (!source) return false;
  const sourceChildIds = Array.from(new Set(getChilds(source.destinationId, rights).map(c => c.id)));
  return sourceChildIds.includes(rightId);
}

/**
 * Get rights to display on graph for a rightholder that is not admin:
 * Includes all rights that belongs to the rightholder and their parents includind group childs.
 * Does not include root rights of a an horizontal group that does not belong to the rightholder.
 * @param rightholderId 
 * @param _rights 
 * @returns 
 */
export function getRightsToDisplay(rightholderId: string, _rights: Right[]): Right[] {
  if (!rightholderId) return [];
  const rights = _rights.filter(r => r.rightholderId === rightholderId);
  const rightsToDisplay: Right[] = [];
  for (const right of rights) {
    const parents = getParents(right.id, _rights);
    if (right.groupId) {
      const group = _rights.find(r => r.id === right.groupId);
      parents.push(group);
      const childs = _rights.filter(r => r.groupId === group.id && r.rightholderId === right.rightholderId);
      parents.push(...childs);

      if (group.groupId) {
        const subGroup = _rights.find(r => r.id === group.groupId);
        parents.push(subGroup);
        const subChilds = _rights.filter(r => r.groupId === subGroup.id && r.rightholderId === right.rightholderId);
        parents.push(...subChilds);
      }
    } else {
      rightsToDisplay.push(right);
    }

    for (const parent of parents) {
      if (!rightsToDisplay.find(r => r.id === parent.id)) {
        rightsToDisplay.push(parent);
      }
    }
  }

  return rightsToDisplay;
}

function getParents(rightId: string, rights: Right[]): Right[] {
  const right = rights.find(r => r.id === rightId);
  if (!right) return [];
  let parents: Right[] = [];
  if (right.groupId) {
    const group = rights.find(r => r.id === right.groupId);
    if (group.groupId) {
      const subGroup = rights.find(r => r.id === group.groupId);
      parents = _getParents(subGroup, rights);
    } else {
      parents = _getParents(group, rights);
    }
  } else {
    parents = _getParents(right, rights);
  }

  const parentIds = Array.from(new Set(parents.map(p => p.id)));
  return parentIds.map(id => parents.find(p => p.id === id));
}

function _getParents(right: Right, rights: Right[]): Right[] {
  if (!right) return [];
  const parents = right.nextIds.map(id => rights.find(r => r.id === id));
  if (!parents.length) return [];
  const parentGroups = parents.filter(p => isGroup(p));
  for (const group of parentGroups) {
    const childs = rights.filter(r => r.groupId === group.id);
    parents.push(...childs);
    const subGroups = childs.filter(c => isGroup(c));
    for (const subGroup of subGroups) {
      parents.push(...rights.filter(r => r.groupId === subGroup.id));
    }
  }
  return parents.concat(parents.map(p => _getParents(p, rights)).flat());
}

function isGroup(right?: Right): boolean {
  if (!right) return false;
  return ['horizontal', 'vertical'].includes(right.type);
}

function _getChilds(rightId: string, rights: Right[]): Right[] {
  if (!rightId) return [];
  const childs = rights.filter(r => r.nextIds.includes(rightId));
  if (!childs.length) return [];
  return childs.concat(childs.map(c => getChilds(c.id, rights)).flat());
}

/**
 * Return expense types defined in conditions of a right
 * @param right 
 * @returns 
 */
export function getRightExpenseTypes(right: Right) {
  const conditions = getRightConditions(right);
  const conditionsWithTarget = conditions.filter(c => isConditionWithTarget(c)) as ConditionWithTarget[];

  const expenseTargets = conditionsWithTarget
    .map(c => (typeof c.payload.target === 'object' && c.payload.target.in === 'expense') ? c.payload.target.id : undefined)
    .filter(id => !!id);

  return Array.from(new Set(expenseTargets));
}

export function getRightsExpenseTypes(rights: Right[]) {
  return rights.map(r => getRightExpenseTypes(r)).flat();
}

export function copyVerticalConditions(createdId: string, duplicatedId: string, newRights: Right[], existingRights: Right[]) {
  const subMembers = newRights.filter(r => r.groupId === createdId);
  const duplicatedSubMembers = existingRights.filter(r => r.groupId === duplicatedId);
  subMembers.forEach(subMember => {
    const newSubMember = newRights.find(r => r.id === subMember.id);
    const duplicatedSubMember = duplicatedSubMembers.find(r => r.order === subMember.order);
    newSubMember.conditions = duplicatedSubMember.conditions;
    newSubMember.version = duplicatedSubMember.version;
  });
}

export function copyRightConditions(createdId: string, duplicatedId: string, newRights: Right[], existingRights: Right[]) {
  const newRight = newRights.find(r => r.id === createdId);
  const duplicatedRight = existingRights.find(r => r.id === duplicatedId);
  if (duplicatedRight) {
    newRight.conditions = duplicatedRight.conditions;
    newRight.version = duplicatedRight.version;
  }
}

export function copyHorizontalConditions(createdId: string, duplicatedId: string, newRights: Right[], existingRights: Right[], copyOf: string) {
  const members = newRights.filter(r => r.groupId === createdId);
  const duplicatedMembers = existingRights.filter(r => r.groupId === duplicatedId);
  members.forEach(member => {
    const newMember = newRights.find(r => r.id === member.id);
    if (newMember.type === 'vertical') {
      const duplicatedMember = duplicatedMembers.find(r => r.name === member.name.replace(copyOf, ''));
      if (duplicatedMember) {
        copyVerticalConditions(newMember.id, duplicatedMember.id, newRights, existingRights);
      }
    } else {
      const duplicatedMember = duplicatedMembers.find(r => r.name === member.name.replace(copyOf, ''));
      if (duplicatedMember) {
        newMember.conditions = duplicatedMember.conditions;
        newMember.version = duplicatedMember.version;
      }
    }
  });
}

export interface PercentConfig {
  percent: number;
  calculatedOn: 'previous' | 'receipt' | 'shadow';
  retroactively: boolean;
  receiptId?: string;
  condition?: TakeFromUntilConditions;
}

export function getPercentConfig(right: Right): PercentConfig {
  const cond = getTakeFromUntilCondition(getRightConditions(right));
  const original = right?.percent || 0;
  const precision = 100;
  return {
    percent: cond?.payload.percent ? toFixedPercentage(cond?.payload.percent, precision) : toFixed(original, precision),
    calculatedOn: cond ? 'receipt' : 'previous',
    retroactively: cond?.name === 'takefromSourceUntil' || false,
    receiptId: cond?.payload.sourceId || '',
    condition: cond
  }
}

export function getActualHorizontalPercent(horizontalId: string, state: TitleState, rights: Right[]) {
  const members = rights.filter(r => r.groupId === horizontalId);

  const sourceId = 'test';
  const payload: IncomeAction = {
    id: 'foo',
    contractId: '',
    from: sourceId,
    to: '',
    amount: 0,
    date: new Date(),
    territories: [],
    medias: [],
  }

  const checkedIds: string[] = [];

  for (const member of members) {
    if (!nodeExists(state, member.id)) continue;
    const node = getNode(state, member.id);

    if (isVerticalGroup(state, node)) {
      for (const childId of node.children) {
        if (!nodeExists(state, childId)) continue;
        const childNode = getNode(state, childId);
        if (isRight(state, childNode)) {
          const right = { ...childNode, conditions: and(skipTakeFromUntilConditions(getRightConditions(childNode))) };
          const { checked } = checkCondition({ state, right, income: payload, sourceId });

          if (checked) {
            checkedIds.push(childId);
            break;
          }

        }
      }
    } else if (isRight(state, node)) {
      const right = { ...node, conditions: and(skipTakeFromUntilConditions(getRightConditions(node))) };
      const { checked } = checkCondition({ state, right, income: payload, sourceId });
      if (checked) checkedIds.push(node.id);
    }
  }

  if (!nodeExists(state, horizontalId)) return 0;
  const currentNode = getNode(state, horizontalId);
  const checkedRights = rights.filter(r => checkedIds.includes(r.id));
  const nodePercentages = checkedRights.map(r => getPercentConfig(r).percent);
  const totalPercent = (sum(nodePercentages) / 100) * currentNode.percent;
  return toFixedPercentage(totalPercent);

}
